import React, { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, useForm } from 'react-hook-form';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import useTranslation from '../../../../../Hooks/useTranslation';
import FinalStepFields, {
	getFinalStepValidationSchema,
} from '../FieldsForms/FinalStepFields';
import MothersHealthFields, {
	getMothersHealthFieldsValidationSchema,
} from '../FieldsForms/MothersHealthFields';
import { getDateDifference } from '../utils/date';
import useAdditionalWeightInPregnancy from '../hooks/useAdditionalWeightInPregnancy';
import {
	useQuestionnaireResultItemsQuery,
	useSubmitQuestionnaireResultMutation,
} from '../../../../../graphql/graphql';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import { FieldNames } from '../../../../../Services/HealthBabyPlanDataContext';
import { useHealthPlanContext } from '../../../../../Services/HealthPlanContext';
import {
	QUESTIONNAIRE_ENTRY_CODE,
	QUESTIONNAIRE_REGULAR_CODE,
} from '../../../constants';
import useNotification from '../../../../../Hooks/useNotification.hook';
import useMothersLastWeight from '../hooks/useMothersLastWeight';
import useMothersWeightBeforePregnancy from '../hooks/useMothersWeightBeforePregnancy';
import { useEffectAsync } from '../../../../../helpers';
import { useModalContext } from '../../../../Modal/Services/ModalContext';
import OverlayLoading from '../../../../../Components/OverlayLoading';
import { TreatmentFormModalContextKeys } from '../../../../../Components/Treatment/TreatmentFormModalContextKeys';
import { getMomentDate } from '../../../../../Utils/dates.util';
import { getTimeWithTimezoneOfSpecificDateAsString } from '../../../../Base/Helpers/timezone.helper';
import FormControl from '../../../../../Components/Form/FormControl';
import Checkbox from '../../../../../Components/Base/Checkbox/Checkbox';
import PatientNoConsentProvided from '../../../../Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';
import usePatientConsent from '../../../../../Components/PatientProfile/Hooks/usePatientConsent';

const UNFILLED_FIELDS = [
	'mothersHealth.ctg.value',
	'mothersHealth.ctg.label',
	'mothersHealth.actualWeight',
	'mothersHealth.systolicBloodPressure',
	'mothersHealth.diastolicBloodPressure',
];

interface Props {
	patientId: string;
	workplaceId: string;
	onSubmit: () => void;
}

const RegularExaminationSection: React.FC<Props> = ({
	patientId,
	workplaceId,
	onSubmit,
}) => {
	const { t } = useTranslation();
	const [isFormSubmitting, setIsFormSubmitting] = useState(false);
	const { healthPlanUser } = useHealthPlanContext();
	const { showNotificationSuccess } = useNotification();
	const { getMothersLastWeight } = useMothersLastWeight();
	const { getMothersWeightBeforePregnancy } = useMothersWeightBeforePregnancy();
	const [weightBeforePregnancy, setWeightBeforePregnancy] = useState<
		number | undefined
	>(undefined);
	const { isConsentProvided } = usePatientConsent(patientId);
	const [isExaminationTypeSelected, setIsExaminationTypeSelected] =
		useState(false);

	useEffectAsync(async () => {
		setWeightBeforePregnancy(await getMothersWeightBeforePregnancy(patientId));
	}, []);

	const { registerFormByKey, unregisterFormsByKey } = useModalContext();

	const {
		data,
		isLoading: areQuestionnaireResultItemsLoading,
		refetch: refetchQuestionnaireResultItems,
	} = useQuestionnaireResultItemsQuery({
		patientId,
		questionnaireCode: QUESTIONNAIRE_ENTRY_CODE,
	});

	const [
		submitQuestionnaireResult,
		{ isLoading: isQuestionnaireResultSubmitting },
	] = useSubmitQuestionnaireResultMutation();

	const formMethods = useForm({
		defaultValues: {}, // TODO needs to be set otherwise it will not work
		resolver: yupResolver(
			getMothersHealthFieldsValidationSchema(t).concat(
				getFinalStepValidationSchema(t),
			),
		),
	});

	useEffect(() => {
		formMethods.setValue(
			`${FieldNames.MOTHERS_HEALTH}.doctor`,
			//@ts-ignore
			{
				//@ts-ignore
				value: healthPlanUser?.doctor?.id,
			},
		);
	}, [healthPlanUser?.doctor?.id]);

	const setFormValues = useCallback(() => {
		//@ts-ignore
		data?.questionnaireResultItems?.data?.forEach(({ question, valueJson }) => {
			if (!UNFILLED_FIELDS.includes(question.key)) {
				formMethods.setValue(question.key, valueJson.rawValue);
			}
		});
	}, [JSON.stringify(data?.questionnaireResultItems?.data)]);

	useEffect(() => {
		setFormValues();
	}, [setFormValues]);

	const isLoading =
		areQuestionnaireResultItemsLoading ||
		isQuestionnaireResultSubmitting ||
		isFormSubmitting;

	const values = formMethods.watch();
	const mothersHealthValues = formMethods.watch(FieldNames.MOTHERS_HEALTH);

	/*useEffect(() => {
        //@ts-ignore
        if (
            mothersHealthValues?.edemas?.length >= 2 &&
            mothersHealthValues.edemas?.[0].value === 'absent'
        ) {
            formMethods.setValue(
                //@ts-ignore
                `${FieldNames.MOTHERS_HEALTH}.edemas`,
                //@ts-ignore
                mothersHealthValues.edemas.filter(({ value }) => value !== 'absent'),
            );
        }

        if (
            //@ts-ignore
            mothersHealthValues?.edemas?.length > 1 &&
            //@ts-ignore
            mothersHealthValues?.edemas?.pop().value === 'absent'
        ) {
            //@ts-ignore
            formMethods.setValue(`${FieldNames.MOTHERS_HEALTH}.edemas`, [
                {
                    value: 'absent',
                    label: t('base.absent'),
                },
            ]);
        }
    }, [
        JSON.stringify(
            //@ts-ignore
            mothersHealthValues?.edemas,
        ),
    ]);*/

	const { additionalWeightInPregnancy } = useAdditionalWeightInPregnancy({
		//@ts-ignore
		weighBeforePregnancy:
			values?.[FieldNames.MOTHERS_HEALTH]?.weighBeforePregnancy ??
			weightBeforePregnancy,
		//@ts-ignore
		actualWeight: values?.[FieldNames.MOTHERS_HEALTH]?.actualWeight,
	});

	const { weeks, days } = getDateDifference({
		date:
			//@ts-ignore
			values?.[FieldNames.FINAL_STEP]?.careDate,
	});

	const getAdditionalValues = async () => {
		if (
			values?.[FieldNames.MOTHERS_HEALTH]?.actualWeight &&
			additionalWeightInPregnancy
		) {
			return {
				additionalWeightInPregnancy,
				additionalWeightSinceLastMeasure: String(
					(
						Number(values?.[FieldNames.MOTHERS_HEALTH]?.actualWeight) -
						Number(await getMothersLastWeight(patientId))
					).toFixed(1),
				),
			};
		}
	};

	const handleSubmitForm = async (formValues: FieldValues) => {
		unregisterFormsByKey([
			TreatmentFormModalContextKeys.MEDDI_BABY_REGULAR_EXAMINATION,
		]);
		setIsFormSubmitting(true);
		const ingestion = [
			{
				key: 'weight',
				value: formValues.mothersHealth.actualWeight,
			},
			{
				key: 'blood_pressure_systolic',
				value: formValues.mothersHealth.systolicBloodPressure,
			},
			{
				key: 'blood_pressure_diastolic',
				value: formValues.mothersHealth.diastolicBloodPressure,
			},
		];

		formValues.mothersHealth.timeOfExamination =
			getTimeWithTimezoneOfSpecificDateAsString({
				time: getMomentDate(formValues.mothersHealth.timeOfExamination),
				date: getMomentDate(formValues.mothersHealth.dateOfExamination),
			});

		try {
			const additionalValues = await getAdditionalValues();

			await submitQuestionnaireResult({
				input: {
					data: {
						...formValues,
						...additionalValues,
					},
					ingestion: ingestion,
					questionnaireCode: QUESTIONNAIRE_REGULAR_CODE,
					userId: patientId,
					hidden:
						formValues?.mothersHealth?.examinationType?.value === 'remote',
					addToPatientECard: formValues?.addToPatientECard ?? false,
				},
			});

			formMethods.reset();
			await refetchQuestionnaireResultItems();
			setFormValues();

			onSubmit();
			showNotificationSuccess({
				content: t('component.regularExamination.formSent'),
			});
		} catch (e) {
			console.error(e);
		} finally {
			setIsFormSubmitting(false);
		}
	};

	const formValues = formMethods.watch();

	useEffect(() => {
		setIsExaminationTypeSelected(
			!!formValues?.mothersHealth?.examinationType?.value,
		);
	}, [formValues]);

	useEffect(() => {
		registerFormByKey(
			TreatmentFormModalContextKeys.MEDDI_BABY_REGULAR_EXAMINATION,
			() => formMethods,
			[
				'mothersHealth.weekOfPregnancy',
				'mothersHealth.dateOfExamination',
				'mothersHealth.timeOfExamination',
				'mothersHealth.weighBeforePregnancy',
				'currentPregnancy',
				'previousPregnancies',
			],
		);
	}, []);

	return (
		<>
			{isLoading && <OverlayLoading />}
			<ScrollContainer>
				<div className='col-12 '>
					<div className='page-panel mt-1'>
						<FormWrapper formMethods={formMethods}>
							<MothersHealthFields
								additionalPregnancyWeight={additionalWeightInPregnancy}
								formMethods={formMethods}
								patientId={patientId}
								showAllFields={isExaminationTypeSelected}
							/>
							{isExaminationTypeSelected && (
								<FinalStepFields
									patientId={patientId}
									workplaceId={workplaceId}
									dateDifference={weeks && days ? `${weeks} + ${days}` : ''}
									doctorId={healthPlanUser?.doctor?.id}
								/>
							)}
							{isConsentProvided ? (
								<div className={'mt-4'}>
									<FormControl name='addToPatientECard' hideRequired={true}>
										<Checkbox tx={'base.addToPatientECard'} />
									</FormControl>
								</div>
							) : (
								<PatientNoConsentProvided patientId={patientId} />
							)}
							<div className='d-flex mt-4 justify-content-between'>
								<SubmitButton
									title={'base.shared.save'}
									onSubmit={handleSubmitForm}
								/>
							</div>
						</FormWrapper>
					</div>
				</div>
			</ScrollContainer>
		</>
	);
};

export default RegularExaminationSection;
